<template>
    <div>
        <div v-if="!fetchingOrgUsers" class="d-flex flex-wrap align-center">
            <OrganizationInvite v-if="isOrgManager && orgUsers.length" />
            <v-spacer />
            <v-text-field
                class="mx-2"
                v-show="expand && orgUsers.length"
                placeholder="Search for a user..."
                autofocus
                prepend-inner-icon="mdi-magnify "
                v-model="search"
                solo
                flat
                hide-details
                dense>
                <template v-slot:append>
                    <v-menu offset-y :close-on-content-click="false">
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn v-bind="attrs" v-on="on" small class="mr-n2">
                                <v-icon left>mdi-tune-variant</v-icon>
                                <span>Filters</span>
                            </v-btn>
                        </template>
                        <v-list nav dense>
                            <div v-for="(item, index) in Object.entries(userCheckboxBinders)" :key="index">
                                <v-sheet rounded class="py-2 px-4 my-1">
                                    <v-switch inset dense hide-details class="pa-0 ma-0" v-model="item[1].status">
                                        <template v-slot:label>
                                            <div class="d-flex align-center">
                                                <v-icon left small>{{ item[1].icon }}</v-icon>
                                                <span class="text-capitalize">{{ item[0] }}</span>
                                            </div>
                                        </template>
                                    </v-switch>
                                </v-sheet>
                            </div>
                        </v-list>
                    </v-menu>
                </template>
            </v-text-field>
            <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                    <v-btn v-on="on" @click="$store.dispatch('orgStore/fetchOrgUsers', $route.params.oid)" text icon>
                        <v-icon>mdi-refresh</v-icon>
                    </v-btn>
                </template>
                <span>refresh</span>
            </v-tooltip>
        </div>

        <div v-if="!fetchingOrgUsers">
            <v-data-table
                v-if="orgUsers.length"
                :headers="userHeaders"
                :items="filteredOrgUsers"
                :search="search"
                item-key="uid"
                :items-per-page.sync="itemsPerPage"
                :footer-props="{ 'items-per-page-options': itemsPerPageOptions }"
                class="mt-4">
                <template v-slot:top="{ options, pagination, updateOptions }">
                    <div class="d-flex align-center">
                        <v-spacer></v-spacer>
                        <v-data-footer
                            :pagination="pagination"
                            :options="options"
                            @update:options="updateOptions"
                            :itemsPerPageOptions="itemsPerPageOptions"
                            items-per-page-text="$vuetify.dataTable.itemsPerPageText"
                            class="no-border" />
                    </div>
                </template>
                <template v-slot:[`item.type`]="{ item }">
                    <v-icon>{{ organizationRole(item.role).icon }}</v-icon>
                </template>
                <template v-slot:[`item.user`]="{ item }">
                    <div class="d-flex flex-column py-2">
                        <div class="font-weight-medium">
                            {{ item.full_name }}
                            <v-chip x-small v-if="item.is_active" color="success">Active</v-chip>
                            <v-chip x-small v-else color="error">Inactive</v-chip>
                        </div>
                        <span class="caption mr-3 font-weight-bold">{{ item.email }}</span>
                    </div>
                </template>
                <template v-slot:[`item.role`]="{ item }">
                    <div class="d-flex align-center py-2">
                        <v-chip outlined label small>
                            <v-icon left small>{{ organizationRole(item.role).icon }}</v-icon>
                            <span>{{ organizationRole(item.role).label }}</span>
                        </v-chip>

                        <div class="ml-2">
                            <OrgUserRoleDialog :user="item" />
                        </div>
                    </div>
                </template>
                <template v-slot:[`item.actions`]="{ item }">
                    <v-menu v-if="isOrgManager" left offset-x nudge-top="10">
                        <template v-slot:activator="{ on }">
                            <v-btn v-on="on" text color="secondary" small>
                                <v-icon>more_horiz</v-icon>
                            </v-btn>
                        </template>
                        <v-list nav dense>
                            <UserActions
                                :uid="item.uid.toString()"
                                :ucid="item.ucid.toString()"
                                :isActiveUser="item.is_active"
                                :userName="item.full_name"
                                :hierarchicalLevel="nuvolosObjectTypes.ORGANIZATION"
                                :role="item.role"
                                :orgName="$store.getters['orgStore/orgLongName']"
                                activatorType="listItem"></UserActions>
                        </v-list>
                    </v-menu>
                </template>
                <template v-slot:no-results>
                    <span>No users found.</span>
                </template>
                <template v-slot:no-data>
                    <span>No users found. Consider inviting new users via the "+ Invite" button above.</span>
                </template>
            </v-data-table>

            <div v-else>
                <v-banner two-line>
                    <v-avatar slot="icon" color="white" size="60">
                        <v-icon x-large color="info">mdi-information-outline</v-icon>
                    </v-avatar>
                    <div class="d-flex flex-column">
                        <span class="font-weight-bold secondary--text mb-1">No users found</span>
                        <span>
                            Currently this organization has no additional users.
                            <span v-if="isOrgManager">
                                You can add new organization users via the
                                <v-icon small color="primary">mdi-plus</v-icon>
                                <span class="font-weight-bold secondary--text">invite</span>
                                button above.
                            </span>
                        </span>
                    </div>
                </v-banner>
            </div>

            <v-alert :value="this.error" color="error" icon="mdi-alert-outline" class="mt-4" text>
                <span class="font-weight-bold">An error has occurred</span>
                <div>
                    The user could not be revoked. To learn more about user revoke issues, check the troubleshooting documentation
                    <v-btn
                        small
                        outlined
                        text
                        color="error"
                        href="https://docs.nuvolos.com/troubleshooting/troubleshooting/administration-troubleshooting/i-cant-revoke-a-user-role"
                        target="_blank">
                        here
                    </v-btn>
                </div>
            </v-alert>
        </div>
        <div v-else>
            <v-skeleton-loader class="mx-auto" type="table"></v-skeleton-loader>
        </div>
    </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { enumsData } from '@/mixins/enums'
import { organizationRole } from '@/utils'
import OrganizationInvite from '@/modules/home/components/OrganizationInvite.vue'
const UserActions = () => import('@/components/UserActions')
const OrgUserRoleDialog = () => import('@/modules/home/components/OrgUserRoleDialog.vue')

export default {
    name: 'OrganizationUsers',
    components: { UserActions, OrganizationInvite, OrgUserRoleDialog },
    mixins: [enumsData],
    data() {
        return {
            search: '',
            error: false,
            expand: true,
            userName: '',
            userUcid: '',
            itemsPerPage: 10,
            itemsPerPageOptions: [10, 25, 50, -1],
            userCheckboxBinders: {
                managers: { status: true, icon: organizationRole('manager').icon },
                faculty: { status: true, icon: organizationRole('faculty').icon },
                members: { status: true, icon: organizationRole('member').icon }
            }
        }
    },
    computed: {
        ...mapState('orgStore', ['orgUsers', 'fetchingOrgUsers']),
        ...mapGetters('snapshotStore', ['isDevelopment']),
        ...mapGetters('orgStore', ['isOrgManager']),
        userHeaders() {
            const baseHeaders = [
                { text: 'Type', value: 'type', sortable: false, width: '40px' },
                { text: 'User', value: 'user', sortable: false },
                { text: 'Role', value: 'role', sortable: false }
            ]
            if (this.isOrgManager) {
                baseHeaders.push({ text: 'Actions', value: 'actions', align: 'right', sortable: false, width: '120px' })
            }
            return baseHeaders
        },
        filteredOrgUsers() {
            if (this.orgUsers) {
                const displayedUsers = []
                if (this.userCheckboxBinders.managers.status) {
                    displayedUsers.push(this.roleTypes.ORG_MANAGER)
                }
                if (this.userCheckboxBinders.faculty.status) {
                    displayedUsers.push(this.roleTypes.ORG_FACULTY)
                }
                if (this.userCheckboxBinders.members.status) {
                    displayedUsers.push(this.roleTypes.ORG_MEMBER)
                }
                const filteredUsers = this.orgUsers.filter(user => displayedUsers.includes(user.role))
                return filteredUsers
            }
            return []
        }
    },
    methods: {
        organizationRole
    }
}
</script>
